import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { LineReveal, Reveal } from "@/components/site/motion";
import { WaitlistForm } from "@/components/site/WaitlistForm";
import { Exhibit } from "@/components/site/Exhibit";
import { screen } from "@/data/screens";

const TiltExhibit = () => {
  const reduce = useReducedMotion();
  const rx = useSpring(useMotionValue(3), { stiffness: 120, damping: 18 });
  const ry = useSpring(useMotionValue(-5), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 10);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 8);
  };
  const onLeave = () => { rx.set(3); ry.set(-5); };
  return (
    <motion.div
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={reduce ? undefined : { rotateX: rx, rotateY: ry, transformPerspective: 1400 }}
      className="exhibit-frame"
      data-testid="hero-tilt-exhibit"
    >
      <Exhibit shot={screen("dashboard")} plate="I" aspect="aspect-[4/3]" eager />
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const gridY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  return (
    <section ref={ref} className="relative overflow-hidden bg-parchment" data-testid="hero-section">
      <motion.div style={{ y: gridY }} className="hairline-grid pointer-events-none absolute inset-0 opacity-70" aria-hidden="true" />
      <div className="container-x relative grid items-center gap-14 py-16 sm:py-24 lg:grid-cols-12 lg:gap-10 lg:py-28">
        <div className="lg:col-span-5">
          <p className="eyebrow">Accounting for churches</p>
          <LineReveal
            lines={["Faithful stewardship,", "carefully recorded."]}
            className="mt-6 font-display text-5xl font-semibold leading-[1.02] tracking-tight text-navy sm:text-6xl lg:text-[4.4rem]"
          />
          <Reveal delay={0.4}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-slate-600 sm:text-lg">
              Grace Treasury keeps a church's books in one orderly ledger: nested funds, gifts split across funds, bank reconciliation and a monthly Treasurer's Report ready for the board.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link to="/demo" className="btn-primary group" data-testid="hero-request-demo-button">
                Request a demo <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/features" className="btn-outline" data-testid="hero-see-features-button">See the features</Link>
            </div>
            <div className="mt-10 border-t border-slate-300/70 pt-6">
              <p className="mb-3 text-sm text-slate-600">Or join the waitlist to hear launch pricing first.</p>
              <WaitlistForm id="hero-waitlist" source="hero" />
            </div>
          </Reveal>
        </div>
        <motion.div style={{ y }} className="lg:col-span-7 lg:pl-6">
          <TiltExhibit />
        </motion.div>
      </div>
    </section>
  );
};
